import React, { useState, useRef, useCallback, useMemo } from "react";
import { FiX, FiArrowRight, FiUpload, FiDatabase, FiUserPlus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import usePost from "../Hooks/usePost.jsx";
import API_ENDPOINTS from "../Config/api.js";
import useUploadCsv from "../Hooks/useUploadCsv.jsx";
import { toast } from "react-toastify";
import ClipLoader from "react-spinners/ClipLoader.js";
// ⬇️ usa la tua funzione di validazione
import isValidInput from "../Utils/isValidInput.js";

/* ---------- UI primitives ---------- */
const cx = (...c) => c.filter(Boolean).join(" ");
const baseInput = "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-gray-400";

function Label({ children }) {
  return <label className="block text-sm font-semibold text-gray-800 mb-1">{children}</label>;
}
function ErrorText({ msg }) {
  if (!msg) return null;
  return <p className="mt-1 text-xs text-red-600">{msg}</p>;
}
function Input({ label, value, onChange, placeholder, type = "text", error, className = "", ctaLabel, handleCta, ...rest }) {
  return (
    <div className={className}>
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        {ctaLabel ? <button onClick={handleCta} className="text-xs hover:text-gray-500 duration-300 underline">{ctaLabel}</button> : null}
      </div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={baseInput}
        {...rest}
      />
      <ErrorText msg={error} />
    </div>
  );
}
function TextArea({ label, value, onChange, placeholder, rows = 3, error, className = "" }) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className={baseInput}
      />
      <ErrorText msg={error} />
    </div>
  );
}
function Select({ label, value, onChange, options = [], error, className = "" }) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={baseInput}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ErrorText msg={error} />
    </div>
  );
}
function ModeCard({ icon, title, desc, onClick }) {
  return (
    <button
      onClick={onClick}
      className="group text-left rounded-xl border border-gray-200 bg-white p-6 hover:shadow-lg transition flex items-start gap-4"
    >
      <div className="rounded-lg p-3 bg-gray-100 group-hover:bg-gray-200 transition">{icon}</div>
      <div>
        <div className="font-semibold">{title}</div>
        <div className="text-sm text-gray-600">{desc}</div>
      </div>
    </button>
  );
}
function Panel({ title, onBack, children }) {
  return (
    <div className="mt-4 flex-1 min-h-0">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">{title}</div>
        {onBack && (
          <button onClick={onBack} className="text-xs underline text-gray-600 hover:text-gray-800">
            Cambia metodo
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

/* ---------- CSV Dropzone ---------- */
function CsvDropzone({ file, setFile, uploading, onUpload, maxSizeMB = 5 }) {

  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [fileKey, setFileKey] = useState(0);

  const resetInput = useCallback(() => {
    setFile(null);
    setDragActive(false);
    if (inputRef.current) {
      try {
        inputRef.current.value = "";
      } catch { }
    }
    setFileKey((k) => k + 1);
  }, [setFile]);

  const handleFiles = useCallback(
    (files) => {
      if (!files || !files.length) return;
      const f = files[0];
      if (!/\.csv$/i.test(f.name)) {
        toast.error("Formato non supportato. Carica un file .csv");
        resetInput();
        return;
      }
      if (f.size > maxSizeMB * 1024 * 1024) {
        toast.error(`File troppo grande (max ${maxSizeMB}MB)`);
        resetInput();
        return;
      }
      setFile(f);
    },
    [maxSizeMB, resetInput, setFile]
  );

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  return (
    <>
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={cx(
          "relative rounded-xl border-2 border-dashed min-h-[200px] flex flex-col items-center justify-center p-6 transition-colors",
          dragActive ? "border-yellow-500 bg-yellow-50" : "border-gray-300 bg-white"
        )}
        onClick={() => inputRef.current && inputRef.current.click()}
        role="button"
        tabIndex={0}
        aria-label="Carica file CSV"
      >
        <input
          key={fileKey}
          ref={inputRef}
          type="file"
          accept=".csv"
          hidden
          onChange={(e) => handleFiles(e.target.files)}
        />

        {!file ? (
          <div className="flex flex-col items-center gap-3 pointer-events-none">
            <FiUpload className="text-3xl" />
            <div className="text-sm text-gray-700 text-center">
              Trascina qui il file <span className="font-medium">.csv</span>
              <br />
              oppure <span className="underline">clicca per selezionarlo</span>
            </div>
            <div className="text-xs text-gray-500">Max {maxSizeMB}MB • Solo CSV</div>
          </div>
        ) : (
          <div className="w-full max-w-2xl pointer-events-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-2 text-sm text-gray-800 truncate">{file.name}</div>
              <div className="ml-auto flex items-center">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    resetInput();
                  }}
                  className="h-10 w-10 grid place-items-center hover:bg-gray-100"
                  aria-label="Rimuovi file"
                >
                  <FiX />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUpload();
                  }}
                  disabled={!file || uploading}
                  className="h-10 w-10 grid place-items-center bg-gray-800 text-white hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Procedi upload"
                  title={!file ? "Seleziona un file CSV" : uploading ? "Caricamento..." : "Carica CSV"}
                >
                  <FiArrowRight />
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Formato supportato: CSV</p>
          </div>
        )}
      </div>

      <p className="mt-4 text-center text-sm text-gray-600">
        Carica un elenco di entità per aggiungere più fornitori in una sola volta.
      </p>
    </>
  );
}

/* ---------- Validazione con valiisValidInputdate ---------- */
function validate(values) {
  const e = {};

  // required
  if (!isValidInput(values.nome, "required")) e.nome = "Campo obbligatorio";
  if (!isValidInput(values.dominio, "required")) e.dominio = "Campo obbligatorio";
  if (!isValidInput(values.emailRif, "required")) e.emailRif = "Campo obbligatorio";
  if (!isValidInput(values.emailContatto, "required")) e.emailContatto = "Campo obbligatorio";
  if (!isValidInput(values.statoContatto, "required")) e.statoContatto = "Campo obbligatorio";

  // almeno uno tra P.IVA/CF valido
  const pivaOk = !!values.piva && isValidInput(values.piva, "piva");
  const cfOk = !!values.cf && isValidInput(values.cf, "cf");
  if (!pivaOk && !cfOk) {
    e.piva = "Inserisci P.IVA valida (11 cifre)";
  } else {
    if (values.piva && !pivaOk) e.piva = "P.IVA non valida (11 cifre).";
    // if (values.cf && !cfOk) e.cf = "CF non valido (16 caratteri alfanumerici).";
  }

  // formato email
  if (values.emailRif && !isValidInput(values.emailRif, "email")) e.emailRif = "Email non valida";
  if (values.emailContatto && !isValidInput(values.emailContatto, "email")) e.emailContatto = "Email non valida";

  // dominio
  if (values.dominio && !isValidInput(values.dominio, "dominio"))
    e.dominio = "Inserisci solo il dominio (senza http/https/www)";

  // telefono opzionale (se presente deve essere valido)
  if (values.telefono && !isValidInput(values.telefono, "telefono")) e.telefono = "Numero non valido";

  return e;
}

/* ---------- Main component ---------- */
export default function CreateSupplierChoice() {

  const navigate = useNavigate();
  const { postData, loading: creating } = usePost();
  const { loading: uploadLoading, uploadCsv } = useUploadCsv();

  const [mode, setMode] = useState("none");
  const [file, setFile] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    nome: "",
    piva: "",
    cf: "",
    indirizzo: "",
    emailRif: "",
    emailContatto: "",
    telefono: "",
    dominio: "",
    nota: "",
    statoContatto: "da contattare",
  });

  // const statoOptions = useMemo(
  //   () => [
  //     { value: "da contattare", label: "da contattare" },
  //     { value: "contattato", label: "contattato" },
  //     { value: "in trattativa", label: "in trattativa" },
  //     { value: "non interessato", label: "non interessato" },
  //   ],
  //   []
  // );

  const handleChange = (key, value) => {
    setForm((s) => {
      const next = { ...s, [key]: value };
      if (showErrors) setErrors(validate(next));
      return next;
    });
  };

  const resetForm = () =>
    setForm({
      nome: "",
      piva: "",
      cf: "",
      indirizzo: "",
      emailRif: "",
      emailContatto: "",
      telefono: "",
      dominio: "",
      nota: "",
      statoContatto: "da contattare",
    });

  const handleConfirm = async () => {
    const v = validate(form);
    setErrors(v);
    setShowErrors(true);
    if (Object.keys(v).length > 0) {
      toast.error("Controlla i campi evidenziati");
      return;
    }

    const payload = {
      partita_iva: form.piva || "",
      denominazione: form.nome,
      email_riferimento: form.emailRif,
      codice_fiscale: form.cf || "",
      indirizzo: form.indirizzo || "",
      dominio_web: form.dominio,
      telefono: form.telefono || "",
      email_contatto: form.emailContatto,
      nota: form.nota || "",
      stato_contatto: "da contattare",
    };

    try {
      await postData(API_ENDPOINTS.creaAzienda, payload);
      toast.success("Fornitore creato correttamente");
      resetForm();
      setShowErrors(false);
      setErrors({});
      navigate("/dashboard");
    } catch {
      toast.error("Si è verificato un errore inatteso");
    }
  };

  const triggerUpload = async () => {
    if (!file) return;
    const { ok, error } = await uploadCsv(API_ENDPOINTS.addCsvFile, file);
    if (!ok) {
      const apiMsg =
        typeof error?.message === "string" ? error.message : error?.message?.errore || "Errore durante l'upload";
      toast.error(`Upload fallito. ${apiMsg}`);
      setFile(null);
      return;
    }
    toast.success("CSV caricato correttamente");
    setFile(null);
    navigate("/dashboard");
  };

function handleCta(params) {
  setForm(prevForm => ({
    ...prevForm,       // copia tutte le proprietà esistenti
    cf: prevForm.piva  // aggiorna cf con il valore di piva
  }));
}


  return (
    <div className="h-full min-h-0 flex flex-col p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 shrink-0">
        <div>
          <h1 className="text-2xl font-semibold tracking-wide">
            CREA <span className="font-bold">NUOVO FORNITORE</span>
          </h1>
          <p className="mt-2 text-sm text-gray-600">Scegli come aggiungere i fornitori da monitorare.</p>
        </div>
        <button
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 hover:shadow-md transition-shadow"
        >
          CANCEL
        </button>
      </div>

      {/* Selettore metodo */}
      {mode === "none" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ModeCard
            icon={<FiUserPlus className="text-xl" />}
            title="Inserimento singolo"
            desc="Aggiungi un singolo fornitore con i campi anagrafici."
            onClick={() => setMode("manual")}
          />
          <ModeCard
            icon={<FiDatabase className="text-xl" />}
            title="Importa da CSV"
            desc="Carica un elenco per aggiungere più fornitori in una volta."
            onClick={() => setMode("csv")}
          />
        </div>
      )}

      {/* Manual (tutto in 2 colonne) */}
      {mode === "manual" && (
        <Panel title="Metodo: Inserimento singolo" onBack={() => setMode("none")}>
          <div className="w-full h-full bg-gray-50 rounded-lg p-6 sm:p-8 text-left flex flex-col border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 min-h-0 overflow-auto">
              <Input
                label="Denominazione / Nome completo"
                value={form.nome}
                onChange={(v) => handleChange("nome", v)}
                placeholder="Es. Azienda S.r.l."
                error={showErrors && errors.nome}
              />
              <Input
                label="Indirizzo"
                value={form.indirizzo}
                onChange={(v) => handleChange("indirizzo", v)}
                placeholder="Via/Piazza, numero civico, città"
                error={showErrors && errors.indirizzo}
              />

              <Input
                label="Partita IVA"
                value={form.piva}
                onChange={(v) => handleChange("piva", v)}
                placeholder="11 cifre"
                error={showErrors && errors.piva}
              />

              <Input
                label="Codice Fiscale"
                value={form.cf}
                onChange={(v) => handleChange("cf", v)}
                placeholder="16 caratteri alfanumerici"
                error={showErrors && errors.cf}
                ctaLabel={'Copia da P.Iva'}
                handleCta={handleCta}
              />

              <Input
                label="Dominio"
                value={form.dominio}
                onChange={(v) => handleChange("dominio", v)}
                placeholder="es. azienda.com (senza http/https)"
                error={showErrors && errors.dominio}
              />
              <Input
                label="Telefono"
                value={form.telefono}
                onChange={(v) => handleChange("telefono", v)}
                placeholder="+39 0123 456789"
                error={showErrors && errors.telefono}
              />

              <Input
                type="email"
                label="Email di riferimento"
                value={form.emailRif}
                onChange={(v) => handleChange("emailRif", v)}
                placeholder="es. info@azienda.com"
                error={showErrors && errors.emailRif}
              />

              <Input
                type="email"
                label="Email contatto"
                value={form.emailContatto}
                onChange={(v) => handleChange("emailContatto", v)}
                placeholder="es. commerciale@azienda.com"
                error={showErrors && errors.emailContatto}
              />

              {/* <Select
                className="md:col-span-2"
                label="Stato contatto"
                value={form.statoContatto}
                onChange={(v) => handleChange("statoContatto", v)}
                options={statoOptions}
                error={showErrors && errors.statoContatto}
              /> */}

              <TextArea
                className="md:col-span-2"
                label="Nota"
                value={form.nota}
                onChange={(v) => handleChange("nota", v)}
                placeholder="Aggiungi una nota opzionale"
                error={showErrors && errors.nota}
              />
            </div>

            <div className="mt-6 flex items-center justify-end gap-3 md:col-span-2">
              <button
                onClick={() => {
                  resetForm();
                  setShowErrors(false);
                  setErrors({});
                }}
                className="px-4 py-2 text-sm border rounded-md hover:shadow-md transition"
                disabled={creating}
              >
                CANCELLA
              </button>
              <button
                onClick={handleConfirm}
                disabled={creating}
                className={cx(
                  "px-4 py-2 text-sm rounded-md flex items-center gap-2 transition relative",
                  !creating ? "bg-gray-700 text-white hover:shadow-md" : "bg-gray-300 text-gray-600 cursor-not-allowed"
                )}
                title={creating ? "Invio in corso..." : "Conferma"}
              >
                <span className={creating ? "invisible" : "block"}>CONFERMA</span>
                {creating && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <ClipLoader size={16} color="#fff" />
                  </span>
                )}
              </button>
            </div>
          </div>
        </Panel>
      )}

      {/* CSV */}
      {mode === "csv" && (
        <Panel title="Metodo: Import da CSV" onBack={() => setMode("none")}>
          <CsvDropzone file={file} setFile={setFile} uploading={uploadLoading} onUpload={triggerUpload} />
        </Panel>
      )}
    </div>
  );
}
